import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import {
  ICliente,
  ICreateCliente,
  IQueryParam,
  IUpdateCliente,
  IUpdateClienteConReemplazos,
  IUsoCategoriasCliente,
} from 'modelos/src';
import { HttpApiAdminService } from '../../auxiliares/servicios/http-api.service';
import { HelperService } from '../../auxiliares/servicios/helper.service';
import { StorageService } from '../../auxiliares/servicios/storage.service';

/**
 * Carpeta de las imagenes override de UNA categoria dentro del cliente.
 *
 * El path en GCS es determinista (`img/<ENV>/<cliente>/<categoria>/<slot>`), asi que la carpeta
 * TIENE que llevar el id: dos categorias del mismo cliente comparten `nombre` y solo se distinguen
 * por `variante` — es literalmente lo que produce "duplicar categoria" —, y con el nombre solo se
 * pisaban las imagenes entre si. El nombre se deja adelante para que el bucket siga siendo legible.
 */
export function carpetaCategoria(
  nombre: string | undefined,
  idCategoria: string | undefined,
): string {
  return `${nombre || 'cat'}-${idCategoria || 'sin-id'}`;
}

@Injectable({
  providedIn: 'root',
})
export class ClientesService {
  private http = inject(HttpApiAdminService);
  private helper = inject(HelperService);
  private storage = inject(StorageService);

  public listar(
    queryParams?: IQueryParam,
  ): Observable<{ totalCount: number; datos: ICliente[] }> {
    const params = this.helper.getQueryParams(queryParams);
    return this.http.get(`/clientes`, { params });
  }

  public crear(dato: ICreateCliente): Observable<ICliente> {
    return this.http.post(`/clientes`, dato);
  }

  public subirImagen(
    file: File,
    nombreCLiente: string,
    nombreImagen: string,
    nombreCategoria?: string,
  ): Observable<{ url: string }> {
    return from(
      this.storage.subirImagenCliente(
        file,
        nombreCLiente,
        nombreImagen,
        nombreCategoria,
      ),
    );
  }

  public listarPorId(id: string): Observable<ICliente> {
    return this.http.get(`/clientes/${id}`);
  }

  /**
   * Uso de cada categoria del cliente: cuantos vecinos la tienen y en que estado. Una sola
   * llamada alimenta toda la pestania Categorias.
   */
  public usoCategorias(id: string): Observable<IUsoCategoriasCliente> {
    return this.http.get(`/clientes/${id}/uso-categorias`);
  }

  /**
   * Si el update saca categorias del cliente, `dato.reemplazosCategorias` dice a que categoria
   * pasan los vecinos que las tenian vigentes. Sin eso el backend responde 409.
   */
  public editar(
    id: string,
    dato: IUpdateClienteConReemplazos,
  ): Observable<ICliente> {
    return this.http.put(`/clientes/${id}`, dato);
  }

  public eliminar(id: string): Observable<void> {
    return this.http.delete(`/clientes/${id}`);
  }

  public desactivar(id: string): Observable<ICliente> {
    return this.http.put(`/clientes/desactivar/${id}`, {});
  }

  public activar(id: string): Observable<ICliente> {
    return this.http.put(`/clientes/activar/${id}`, {});
  }
}
